/**
 * Time Trial Leaderboard Component
 * 
 * Displays sorted leaderboard for a time trial event
 * - Shows rank, rider name, time, trail, direction
 * - Highlights top 3 with medal icons
 * - Admin-only delete buttons
 * - Empty state when no results
 * 
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5
 */

import { useEffect, useState, useCallback } from 'react';
import { restClient, TimeTrial, Rider } from '../../../api/rest-client';
import { useAuth } from '../../../auth/AuthProvider';
import { Card } from '../../common/Card';
import { Button } from '../../common/Button';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { useUIStore } from '../../../store/uiStore';
import { formatTimeTrialTime } from '../../../utils/timeTrials';

interface TimeTrialLeaderboardProps {
  eventId: string;
  eventName?: string;
  trailName?: string;
  direction?: string;
  showRecordButton?: boolean;
  onRecordClick?: () => void;
  onEditClick?: (trial: TimeTrial) => void;
}

interface LeaderboardEntry extends TimeTrial {
  rank: number;
  riderName: string;
}

export function TimeTrialLeaderboard({
  eventId,
  eventName,
  trailName,
  direction,
  showRecordButton = false,
  onRecordClick,
  onEditClick
}: TimeTrialLeaderboardProps) {
  const { isAdmin } = useAuth();
  const showToast = useUIStore(state => state.showToast);
  const [timeTrials, setTimeTrials] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [riders, setRiders] = useState<Rider[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  /**
   * Fetch riders for name lookup
   */
  const fetchRiders = useCallback(async () => {
    try {
      const ridersList = await restClient.getRiders();
      setRiders(ridersList);
    } catch (err) {
      // Non-critical error, just log it
      console.error('Failed to fetch riders:', err);
    }
  }, []);

  /**
   * Fetch time trials for this event
   */
  const fetchTimeTrials = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const trials = await restClient.getTimeTrials({ event_id: eventId });
      
      // Sort by time (fastest first)
      const sorted = [...trials].sort((a, b) => a.time - b.time);
      
      // Add rank and rider name
      const withRankAndName = sorted.map((trial, index) => {
        const rider = riders.find(r => r.rider_id === trial.rider_id);
        return {
          ...trial,
          rank: index + 1,
          riderName: rider?.name || trial.rider_id
        };
      });

      setTimeTrials(withRankAndName);
    } catch (err) {
      console.error('Failed to fetch time trials:', err);
      setError('Failed to load time trial results. Please try again.');
      showToast('error', 'Failed to load time trial results');
    } finally {
      setLoading(false);
    }
  }, [eventId, riders, showToast]);

  /**
   * Handle delete time trial
   */
  const handleDelete = useCallback(async (trial: LeaderboardEntry) => {
    if (!isAdmin) {
      showToast('error', 'Only admins can delete time trials');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete ${trial.riderName}'s time of ${formatTimeTrialTime(trial.time)}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeletingId(trial.trial_id);
      await restClient.deleteTimeTrial(trial.event_id, trial.rider_id);
      showToast('success', 'Time trial deleted successfully');
      
      // Refresh leaderboard
      await fetchTimeTrials();
    } catch (err) {
      console.error('Failed to delete time trial:', err);
      showToast('error', 'Failed to delete time trial. Please try again.');
    } finally {
      setDeletingId(null);
    }
  }, [isAdmin, fetchTimeTrials, showToast]);

  /**
   * Get medal icon for top 3
   */
  const getMedalIcon = (rank: number): string | null => {
    switch (rank) {
      case 1:
        return '🥇';
      case 2:
        return '🥈';
      case 3:
        return '🥉';
      default:
        return null;
    }
  };

  // Fetch riders on mount
  useEffect(() => {
    fetchRiders();
  }, [fetchRiders]);

  // Fetch time trials when riders are loaded
  useEffect(() => {
    if (riders.length > 0) {
      fetchTimeTrials();
    }
  }, [riders, fetchTimeTrials]);

  if (loading) {
    return (
      <Card>
        <div className="flex justify-center items-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={fetchTimeTrials} variant="secondary">
            Try Again
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Time Trial Results
            </h3>
            {eventName && (
              <p className="text-sm text-gray-600 mt-1">{eventName}</p>
            )}
            {(trailName || direction) && (
              <p className="text-sm text-gray-500 mt-1">
                {trailName}
                {trailName && direction && ' - '}
                {direction}
              </p>
            )}
            <p className="text-sm text-gray-500 mt-1">
              {timeTrials.length} {timeTrials.length === 1 ? 'participant' : 'participants'}
            </p>
          </div>
          
          {showRecordButton && isAdmin && onRecordClick && (
            <Button onClick={onRecordClick} size="small">
              Record Time
            </Button>
          )}
        </div>

        {/* Leaderboard Table */}
        {timeTrials.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No results recorded yet.</p>
            {showRecordButton && isAdmin && (
              <p className="mt-2 text-sm">Click "Record Time" to add results.</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rider
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  {!trailName && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trail
                    </th>
                  )}
                  {!direction && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Direction
                    </th>
                  )}
                  {isAdmin && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {timeTrials.map((trial) => (
                  <tr key={trial.trial_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      <span className="flex items-center space-x-2">
                        {getMedalIcon(trial.rank) && (
                          <span className="text-lg">{getMedalIcon(trial.rank)}</span>
                        )}
                        <span>{trial.rank}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {trial.riderName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {formatTimeTrialTime(trial.time)}
                    </td>
                    {!trailName && (
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {trial.trail_name}
                      </td>
                    )}
                    {!direction && (
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {trial.direction}
                      </td>
                    )}
                    {isAdmin && (
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm space-x-2">
                        {onEditClick && (
                          <Button
                            onClick={() => onEditClick(trial)}
                            variant="secondary"
                            size="small"
                          >
                            Edit
                          </Button>
                        )}
                        <Button
                          onClick={() => handleDelete(trial)}
                          variant="danger"
                          size="small"
                          disabled={deletingId === trial.trial_id}
                        >
                          {deletingId === trial.trial_id ? 'Deleting...' : 'Delete'}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}
